import { useState } from 'react';
import { trpc } from 'src/trpc';
import { safeMoneyFromCents } from '@libs/services/money';
import { ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PriceRowProps {
  label: string;
  amount: string;
  className?: string;
}

const PriceRow = ({ label, amount, className }: PriceRowProps) => (
  <div className={cn('flex flex-row justify-between w-full', className)}>
    <span className="text-text-primary font-medium text-sm">{label}</span>
    <span className="font-medium text-text-primary text-sm">{amount}</span>
  </div>
);

interface LocationSurchargeProps {
  cartId: string;
}

export const LocationSurcharge = ({ cartId }: LocationSurchargeProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    data: surchargeData,
    isLoading,
    error,
  } = trpc.admin.cart.calculateDistanceSurcharge.useQuery({ cartId }, { retry: false });

  if (isLoading) {
    return (
      <div className="card flex flex-col justify-start w-full mt-2">
        <PriceRow label="Distance Surcharge" amount={safeMoneyFromCents(0).toFormat()} />

        <div className="text-xs text-muted w-full mt-1 flex items-center gap-1">
          <Loader2 className="animate-spin h-4 w-4 text-primary" />
          <span>Loading calculation...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card flex flex-col justify-start w-full mt-2">
        <PriceRow label="Distance Surcharge" amount={safeMoneyFromCents(0).toFormat()} />
        <div className="text-xs text-muted w-full mt-1">
          <span>Error loading surcharge data</span>
        </div>
      </div>
    );
  }

  const hasSurcharge = surchargeData && surchargeData.surchargeInCents > 0;
  const surchargeAmount = surchargeData?.surchargeInCents || 0;

  // Calculate values from surcharge data
  const totalDistance = surchargeData?.distanceInMiles || 0;
  const fourTripDistance = totalDistance * 4;
  const threshold = surchargeData?.organizationPricing.minimumDistanceThresholdInMiles || 0;
  const extraMiles = Math.max(0, fourTripDistance - threshold * 4);
  const pricePerMile = surchargeData?.organizationPricing.surchargePerIncrementInCents || 0;
  const facilityName = surchargeData?.nearestLocation.name || 'facility';

  return (
    <div className="card flex flex-col justify-start w-full mt-2">
      <PriceRow
        label="Distance Surcharge"
        amount={safeMoneyFromCents(surchargeAmount).toFormat()}
      />

      <div className="flex flex-row justify-between w-full">
        <div>
          <div className="text-xs text-muted mt-1 flex items-center gap-1 flex-wrap">
            {hasSurcharge ? (
              <>
                <span>
                  Based on {extraMiles.toFixed(1)} extra miles at{' '}
                  {safeMoneyFromCents(pricePerMile).toFormat()} per mile
                </span>
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-blue-600 hover:text-blue-800 text-xs underline flex items-center gap-1"
                >
                  How it's calculated?
                  <ChevronDown className={cn('w-3 h-3', isExpanded ? 'rotate-180' : '')} />
                </button>
              </>
            ) : (
              <span>No distance surcharge applied to this order</span>
            )}
          </div>
        </div>
      </div>

      {isExpanded && hasSurcharge && (
        <div className="flex flex-col justify-between w-full mt-3 pt-3 border-t border-slate-200">
          <p className="text-muted text-xs leading-relaxed">
            The delivery is <strong>{totalDistance.toFixed(1)} miles</strong> from the{' '}
            {facilityName} facility. The first <strong>{threshold * 4} miles</strong> are included
            at no extra charge. The remaining <strong>{extraMiles.toFixed(1)} miles</strong> are
            charged at <strong>{safeMoneyFromCents(pricePerMile).toFormat()} per mile</strong>.
          </p>
          <div className="mt-2 text-xs text-muted">
            Calculation: ({totalDistance.toFixed(1)} miles × 4 trips) - {threshold * 4} miles
            included = {extraMiles.toFixed(2)} extra miles
          </div>
        </div>
      )}
    </div>
  );
};
